import dayjs from 'dayjs';

import { Locale, ReservationTextType } from 'app/generated/hygraph';
import { AttractionContent } from 'app/hooks/useAttraction';
import {
  AttractionConfig,
  EntitlementReservation,
  LineupAttraction,
  Pass,
  ProductFamily,
  Reservation,
  ReservationModel,
  ReservationOption,
  ReservationRequirement,
  ReservationType,
  TicketCategory,
} from 'app/services/GuestCenterService.types';
import { expandTpl, getTextKey, ReservationTextHelper } from 'app/services/I18n';
import { isAlacarte } from 'app/services/ProductLineupHelper';
import { toYMD } from 'app/utils/time';

export enum ReservationActions {
  ReservePhone = 'reserve-phone',
  ReserveRedirect = 'reserve-redirect',
  Reserve = 'reserve',
  ViewTickets = 'view-tickets',
}

export function isReservable(config: AttractionConfig, lineupAttraction: LineupAttraction) {
  return (
    !config.isClosed &&
    lineupAttraction.reservationModel !== ReservationModel.None &&
    lineupAttraction.reservationRequirement !== ReservationRequirement.NotRequired &&
    (hasTicketingSystem(config) || isRedirect(lineupAttraction))
  );
}

export function hasTicketingSystem(config: Maybe<AttractionConfig>) {
  return !!config?.ticketingSystem;
}

export function isPhone(lineupAttraction: LineupAttraction) {
  return lineupAttraction.reservationModel === ReservationModel.Phone;
}

// when true = model 3/4
export function isRedirect(lineupAttraction: Maybe<LineupAttraction>) {
  return lineupAttraction?.reservationModel === ReservationModel.Redirect;
}

export function redirectURL(
  config: AttractionConfig,
  props?: Record<string, string | number | string[]>
) {
  return expandTpl(config.ticketingSystem?.redirectURL || '', { props });
}

export function phoneNumber(reservationContent: AttractionContent['reservation']): string[] {
  return (
    reservationContent?.texts.find((text) => getTextKey(text) === ReservationTextType.PhoneNumber)
      ?.value ?? ''
  )
    .split('|')
    .map((v) => v.trim());
}

export function userActions(
  config: AttractionConfig,
  lineupAttraction: LineupAttraction
): ReservationActions[] {
  const actions: ReservationActions[] = [];

  if (isPhone(lineupAttraction)) {
    actions.push(ReservationActions.ReservePhone);
  } else if (isReservable(config, lineupAttraction)) {
    actions.push(
      isRedirect(lineupAttraction) ? ReservationActions.ReserveRedirect : ReservationActions.Reserve
    );
  }

  if (lineupAttraction.showViewTickets) {
    actions.push(ReservationActions.ViewTickets);
  }

  return actions;
}

export function barcodeTail(barcode: string): string {
  return barcode.slice(-4);
}

export function prettyBarcode(barcode: string): string {
  return barcode.replace(/(\d{4})/g, '$1 ');
}

export function ticketingDaysOutMax(config?: AttractionConfig): number | undefined {
  const daysOutMax = config?.ticketingSystem?.search?.daysOutMax;
  return daysOutMax === -1 ? undefined : daysOutMax;
}

export function ticketingDaysOutMin(config?: AttractionConfig): number {
  const daysOutMin = config?.ticketingSystem?.search?.daysOutMin;
  return daysOutMin ?? 0;
}

export function allowHold(config?: AttractionConfig): boolean {
  return !!config?.ticketingSystem?.reserve?.allowHold;
}

export function hasOptions(lineupAttraction: LineupAttraction | undefined): boolean {
  return getOptions(lineupAttraction).length > 0;
}

export function getOptions(lineupAttraction: LineupAttraction | undefined): ReservationOption[] {
  return [...(lineupAttraction?.options ?? [])];
}

// the guest center suffixes option keys with the product family (`michiganave-cticket`) while
// the lineup keys them bare (`michiganave`); an exact match wins over the suffixed fallback
export function findReservationOption(
  lineupAttraction: LineupAttraction | undefined,
  optionKey: Maybe<string>,
  familyCode: ProductFamily
): ReservationOption | undefined {
  if (!optionKey) return undefined;

  const options = getOptions(lineupAttraction);
  return (
    options.find((option) => option.optionKey === optionKey) ??
    options.find((option) => `${option.optionKey}-${familyCode}` === optionKey)
  );
}

export function getOptionType(
  lineupAttraction: LineupAttraction | undefined
): ReservationOption['optionType'] {
  return getOptions(lineupAttraction)[0]?.optionType ?? '';
}

export function hasTimeBasedOptions(lineupAttraction: LineupAttraction | undefined): boolean {
  const options = getOptions(lineupAttraction);
  return options.some((option) => option.reservationType === ReservationType.Time);
}

export function allOptionsAreTimeBased(lineupAttraction: LineupAttraction | undefined): boolean {
  const options = getOptions(lineupAttraction);

  // If there are no options, return false
  if (options.length === 0) return false;

  // Check if ALL options are time-based
  return options.every((option) => option.reservationType === ReservationType.Time);
}

export function canCancel(reservation: EntitlementReservation | undefined): boolean {
  if (!reservation) return false;
  if (reservation.disableCancel) return false;
  if (reservation.attractionConfig?.isClosed) return false;
  if (reservation.attractionConfig?.ticketingSystem?.online === false) return false;
  if (!reservation.cancelCutoffUTC) return false;

  // compares current date in UTC against reservation cutoff UTC property. same w/ modify below.
  return dayjs().valueOf() < dayjs(reservation.cancelCutoffUTC).valueOf();
}

export function canModify(reservation: EntitlementReservation | undefined): boolean {
  if (!reservation) return false;
  if (reservation.disableModify !== false) return false;
  if (reservation.attractionConfig?.isClosed) return false;
  if (reservation.attractionConfig?.ticketingSystem?.online === false) return false;
  if (!reservation.modifyCutoffUTC) return false;

  return dayjs().valueOf() < dayjs(reservation.modifyCutoffUTC).valueOf();
}

export function canShowCancelModifyButtons(
  reservation: EntitlementReservation | undefined
): boolean {
  if (!reservation) return false;

  if (isAlacarte(reservation.productLineup)) {
    return false;
  }

  if (!reservation.attractionOrderNumber) {
    return false;
  }

  return true;
}

export function isUpgradeReservation(
  reservation: Pick<Reservation, 'paymentOrder' | 'tickets'>
): boolean {
  return (
    !!reservation.paymentOrder || (reservation.tickets?.some((t) => t.upgradeID != null) ?? false)
  );
}

export function date(dateTime: string) {
  return dayjs(toYMD(dateTime));
}

export function time(dateTime: string, ianaTimezone?: string) {
  return dayjs(dateTime.split('+')[0]).tz(ianaTimezone, true);
}

export function reservationLabel(reservation?: EntitlementReservation) {
  const ticketCategory = reservation?.hasTicketCategories
    ? reservation.ticketCategories[0]
    : undefined;

  if (!reservation?.reservationDateTime) return '';

  const shouldShowTime = showReservationTime(reservation, ticketCategory);
  // Boat attractions show the boarding time instead of the departure time.
  const dateTime =
    (shouldShowTime && reservation.preArrivalTime) || reservation.reservationDateTime;

  return time(dateTime, reservation.attractionConfig?.ianaTimezone).format(
    `dddd, MMM D, YYYY${shouldShowTime ? ', h:mma z' : ''}`
  );
}

export function hasUsage(reservation?: EntitlementReservation) {
  return reservation?.reservationDateTime
    ? time(reservation.reservationDateTime, reservation.attractionConfig?.ianaTimezone).isBefore(
        dayjs()
      )
    : false;
}

export function hasFutureReservation(reservation?: EntitlementReservation) {
  return reservation?.reservationDateTime
    ? time(reservation.reservationDateTime, reservation.attractionConfig?.ianaTimezone).isAfter(
        dayjs()
      )
    : false;
}

export function getOptionContent(
  reservation: Maybe<EntitlementReservation>,
  {
    type,
    rt,
    ticketCategory,
    locale,
  }: {
    type: 'title' | 'details';
    rt: ReservationTextHelper;
    ticketCategory?: TicketCategory;
    locale?: Locale;
  }
) {
  if (!reservation) return '';

  const optionContentKey = reservation.option?.contentKey;
  const categoryContentKey = ticketCategory?.contentKey;

  if (!optionContentKey && !categoryContentKey) return '';

  const props = {
    locale,
    optionTitle: rt(`${optionContentKey}_title`, { locale }),
    optionDetails: rt(`${optionContentKey}_details`, { locale }),
    categoryTitle: rt(`${categoryContentKey}_title`, { locale }),
    categoryDetails: rt(`${categoryContentKey}_details`, { locale }),
  };

  if (categoryContentKey) {
    if (ticketCategory.displayOptionContent && optionContentKey) {
      const key = `${optionContentKey}_${type}`;
      const value = rt(`${key}_template`, props) || rt(key, props);
      if (value) return value;
    }

    const key = `${categoryContentKey}_${type}`;
    const value = rt(`${key}_template`, props) || rt(key, props);
    if (value) return value;
  }

  if (optionContentKey) {
    return rt(`${optionContentKey}_${type}`, props);
  }

  return '';
}

export function getTicketSummaries(
  reservation: Maybe<EntitlementReservation>,
  { rt, locale }: { rt: ReservationTextHelper; locale?: Locale }
): {
  key: string;
  ticketCategory?: TicketCategory;
  option?: ReservationOption;
  optionTitle: string;
  optionDetails?: string;
}[] {
  if (!reservation) return [];

  return reservation.ticketCategories.length > 0
    ? reservation.ticketCategories.map((ticketCategory) => ({
        key: ticketCategory.categoryKey,
        ticketCategory,
        option: reservation.option,
        optionTitle: getOptionContent(reservation, { type: 'title', rt, ticketCategory, locale }),
        optionDetails: getOptionContent(reservation, {
          type: 'details',
          rt,
          ticketCategory,
          locale,
        }),
      }))
    : [
        {
          key: '$default',
          option: reservation.option,
          optionTitle: getOptionContent(reservation, { type: 'title', rt, locale }),
          optionDetails: getOptionContent(reservation, { type: 'details', rt, locale }),
        },
      ];
}

/**
 * The time a single ticket category is visited, which is not always the time the reservation
 * starts: a Shedd 4-D experience, for example, follows general admission later in the day.
 * Returns undefined when the category has no dated tickets, so callers fall back to the
 * reservation time.
 */
export function ticketCategoryDateTime(
  reservation: Maybe<Pick<Reservation, 'tickets'>>,
  ticketCategory?: Pick<TicketCategory, 'categoryKey'>
): string | undefined {
  if (!ticketCategory?.categoryKey) return undefined;

  return (reservation?.tickets ?? [])
    .filter((ticket) => ticket.categoryKey === ticketCategory.categoryKey && !!ticket.visitDateTime)
    .map((ticket) => ticket.visitDateTime as string)
    .sort((a, b) => a.split('+')[0].localeCompare(b.split('+')[0]))[0];
}

export function showReservationTime(
  reservation: { type?: ReservationType; option?: Maybe<ReservationOption> },
  ticketCategory?: TicketCategory,
  lineupAttraction?: LineupAttraction
): boolean {
  const reservationType =
    ticketCategory?.reservationType ??
    reservation?.option?.reservationType ??
    lineupAttraction?.reservationType ??
    reservation?.type;

  // Before option selection, reveal time only when every available option requires it.
  // (only show time step from the start if there are no day-based options to choose from)
  if (!reservation?.option && lineupAttraction) {
    const allTimeBased = allOptionsAreTimeBased(lineupAttraction);
    if (allTimeBased) return true;
  }

  return reservationType === ReservationType.Time;
}

// Helper functions for working with reservations and tickets
export function getReservationPassBarcodes(reservation: Reservation): string[] {
  return (
    reservation.tickets
      ?.map((ticket) => ticket.passBarcode)
      .filter((barcode): barcode is string => Boolean(barcode)) ?? []
  );
}

export function buildPassBarcodeSet(passes: Pass[]): Set<string> {
  return new Set(passes.map((pass) => pass.barcode));
}
